import { onIdTokenChanged } from "firebase/auth";

/**
 * SiliconLogix Cross-Subdomain Session Continuity & Single Sign-On (SSO) Bridge
 * 
 * Manages seamless, secure authentication synchronization across all SiliconLogix domains:
 * - siliconlogix.in (Main Portal)
 * - codebox.siliconlogix.in (CodeBox Simulation & IDE Portal)
 * - admin.siliconlogix.in (Admin Management Console)
 * - localhost / 127.0.0.1 (Local development multi-port setup)
 */

const COOKIE_NAME = "__slx_auth_bridge";
const DB_NAME = "firebaseLocalStorageDb";
const DB_VERSION = 1;
const DB_OBJECTSTORE_NAME = "firebaseLocalStorage";
const DB_DATA_KEYPATH = "fbase_key";
const MAX_SESSION_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days (matches Firebase token refresh window)

// Fallback API key if env is not defined
const DEFAULT_FIREBASE_API_KEY = "AIzaSyC_6jf7QtRdCBglmRmwMjUqfNkIn9wwrg4";

/**
 * Calculates cookie domain dynamically based on current host.
 * For production subdomains (*.siliconlogix.in), returns '.siliconlogix.in' to share across subdomains.
 * For local environments (localhost, 127.0.0.1), returns empty string to allow host-scoped sharing.
 */
export const getCookieDomain = () => {
  if (typeof window === "undefined") return "";
  const hostname = window.location.hostname;
  if (hostname.includes("siliconlogix.in")) {
    return "; domain=.siliconlogix.in";
  }
  return "";
};

/**
 * Encodes an object safely into a URL-friendly UTF-8 Base64 string.
 */
export const encodeSessionPayload = (data) => {
  try {
    const jsonStr = JSON.stringify(data);
    return encodeURIComponent(btoa(unescape(encodeURIComponent(jsonStr))));
  } catch (err) {
    console.error("[SessionBridge] Encoding error:", err);
    return null;
  }
};

/**
 * Decodes a URL-friendly UTF-8 Base64 string back into an object.
 */
export const decodeSessionPayload = (str) => {
  try {
    if (!str) return null;
    const jsonStr = decodeURIComponent(escape(atob(decodeURIComponent(str))));
    return JSON.parse(jsonStr);
  } catch (err) {
    console.warn("[SessionBridge] Decoding error:", err);
    return null;
  }
};

/**
 * Opens or accesses the standard Firebase IndexedDB instance.
 */
const openIndexedDB = () => {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      return reject(new Error("IndexedDB is not supported in this browser environment."));
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DB_OBJECTSTORE_NAME)) {
        db.createObjectStore(DB_OBJECTSTORE_NAME, { keyPath: DB_DATA_KEYPATH });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
};

/**
 * Retrieves an entry directly from Firebase's IndexedDB.
 */
export const getIndexedDBItem = async (key) => {
  try {
    const db = await openIndexedDB();
    return new Promise((resolve) => {
      const tx = db.transaction([DB_OBJECTSTORE_NAME], "readonly");
      const store = tx.objectStore(DB_OBJECTSTORE_NAME);
      const getReq = store.get(key);
      getReq.onsuccess = () => {
        db.close();
        resolve(getReq.result ? getReq.result.value : null);
      };
      getReq.onerror = () => {
        db.close();
        resolve(null);
      };
    });
  } catch (err) {
    return null;
  }
};

/**
 * Stores an entry directly into Firebase's IndexedDB.
 */
export const setIndexedDBItem = async (key, value) => {
  const db = await openIndexedDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([DB_OBJECTSTORE_NAME], "readwrite");
    const store = tx.objectStore(DB_OBJECTSTORE_NAME);
    const putReq = store.put({
      [DB_DATA_KEYPATH]: key,
      value: value,
    });
    putReq.onsuccess = () => resolve();
    putReq.onerror = () => reject(putReq.error);
    tx.oncomplete = () => db.close();
    tx.onerror = () => reject(tx.error);
  });
};

/**
 * Deletes an entry from Firebase's IndexedDB.
 */
export const removeIndexedDBItem = async (key) => {
  try {
    const db = await openIndexedDB();
    return new Promise((resolve) => {
      const tx = db.transaction([DB_OBJECTSTORE_NAME], "readwrite");
      const store = tx.objectStore(DB_OBJECTSTORE_NAME);
      const delReq = store.delete(key);
      delReq.onsuccess = () => resolve();
      delReq.onerror = () => resolve();
      tx.oncomplete = () => db.close();
      tx.onerror = () => resolve();
    });
  } catch (err) {
    // Ignore cleanup error
  }
};

/**
 * Reads the active cross-subdomain session from cookie.
 */
export const readSessionFromCookie = () => {
  if (typeof document === "undefined") return null;
  try {
    const cookies = document.cookie.split(";");
    for (let c of cookies) {
      const trimmed = c.trim();
      if (trimmed.startsWith(`${COOKIE_NAME}=`)) {
        const raw = trimmed.substring(`${COOKIE_NAME}=`.length);
        if (!raw) return null;
        const payload = decodeSessionPayload(raw);
        if (!payload) return null;
        // Check session age limit
        if (payload.ts && Date.now() - payload.ts > MAX_SESSION_AGE_MS) {
          clearSessionCookie();
          return null;
        }
        return payload;
      }
    }
  } catch (err) {
    console.error("[SessionBridge] Error reading session cookie:", err);
  }
  return null;
};

/**
 * Saves the current Firebase user session to the shared subdomain cookie.
 */
export const saveSessionToCookie = (user) => {
  if (typeof document === "undefined" || !user) return;
  try {
    const sessionData = typeof user.toJSON === "function" ? user.toJSON() : user;
    if (!sessionData) return;

    const payload = {
      uid: user.uid,
      email: user.email || "",
      displayName: user.displayName || "",
      ts: Date.now(),
      session: sessionData,
    };

    const encoded = encodeSessionPayload(payload);
    if (!encoded) return;

    const domain = getCookieDomain();
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    const maxAge = Math.floor(MAX_SESSION_AGE_MS / 1000);

    document.cookie = `${COOKIE_NAME}=${encoded}; path=/; max-age=${maxAge}; SameSite=Lax${domain}${secure}`;
  } catch (err) {
    console.error("[SessionBridge] Failed to save session cookie:", err);
  }
};

/**
 * Clears the shared cross-subdomain session cookie on logout.
 */
export const clearSessionCookie = () => {
  if (typeof document === "undefined") return;
  try {
    const domain = getCookieDomain();
    // Clear with subdomain scope
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT${domain}`;
    // Also clear with host scope as fallback
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } catch (err) {
    console.error("[SessionBridge] Failed to clear session cookie:", err);
  }
};

/**
 * Initializes session bridge before the React application mounts.
 * Inspects both URL handoff parameters and the shared subdomain cookie.
 * If a valid authenticated session exists, injects it into IndexedDB & localStorage
 * so Firebase Auth initializes already authenticated on page load.
 */
export const initSessionBridge = async (apiKeyOverride) => {
  if (typeof window === "undefined") return;

  try {
    let syncPayload = null;
    const urlParams = new URLSearchParams(window.location.search);
    const urlSync = urlParams.get("_slx_sync");

    // 1. Direct navigation handoff via URL param
    if (urlSync) {
      syncPayload = decodeSessionPayload(urlSync);
      // Clean query parameter immediately for security and clean address bar
      urlParams.delete("_slx_sync");
      const newSearch = urlParams.toString() ? `?${urlParams.toString()}` : "";
      const cleanUrl = `${window.location.pathname}${newSearch}${window.location.hash}`;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    // 2. If not in URL, read from shared subdomain cookie
    if (!syncPayload) {
      syncPayload = readSessionFromCookie();
    }

    if (!syncPayload || !syncPayload.session || !syncPayload.uid) {
      return;
    }

    // 3. Expiry check
    if (syncPayload.ts && Date.now() - syncPayload.ts > MAX_SESSION_AGE_MS) {
      clearSessionCookie();
      return;
    }

    const apiKey = apiKeyOverride || process.env.REACT_APP_FIREBASE_API_KEY || DEFAULT_FIREBASE_API_KEY;
    const userStorageKey = `firebase:authUser:${apiKey}:[DEFAULT]`;
    const persistenceKey = `firebase:persistence:${apiKey}:[DEFAULT]`;

    // 4. Check if local IndexedDB already has this exact user session
    const existing = await getIndexedDBItem(userStorageKey);
    if (existing && existing.uid === syncPayload.uid) {
      return; // Already matched and up to date
    }

    // 5. Inject session into IndexedDB and localStorage
    const sessionData = syncPayload.session;
    await setIndexedDBItem(userStorageKey, sessionData);
    await setIndexedDBItem(persistenceKey, "LOCAL");

    try {
      localStorage.setItem(userStorageKey, JSON.stringify(sessionData));
      localStorage.setItem(persistenceKey, "LOCAL");
    } catch (e) {
      // LocalStorage access might be blocked in strict private modes; IndexedDB is primary
    }

    // Ensure cookie is fresh on parent domain if brought in via URL
    if (urlSync) {
      saveSessionToCookie({
        uid: syncPayload.uid,
        email: syncPayload.email,
        displayName: syncPayload.displayName,
        toJSON: () => sessionData,
      });
    }

    console.info(`[SessionBridge] Synced session for: ${syncPayload.email || syncPayload.uid}`);
  } catch (err) {
    console.error("[SessionBridge] Synchronization error:", err);
  }
};

/**
 * Attaches real-time authentication state listeners to Firebase Auth.
 * Automatically synchronizes login, logout, and token rotation events across subdomains.
 */
export const attachAuthSyncListener = (authInstance) => {
  if (!authInstance) return () => {};

  // Listen for login, logout, and background ID token refreshes
  const unsubscribe = onIdTokenChanged(authInstance, (user) => {
    if (user) {
      saveSessionToCookie(user);
    } else {
      clearSessionCookie();
    }
  });

  // Cross-tab visibility sync: when tab becomes active, verify cookie presence
  const handleVisibilityChange = () => {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
      const cookieSession = readSessionFromCookie();
      const currentUser = authInstance.currentUser;

      // If user logged out on another subdomain but this tab is still active, sign out
      if (!cookieSession && currentUser) {
        authInstance.signOut().catch(() => {});
      }
    }
  };

  if (typeof window !== "undefined") {
    window.addEventListener("visibilitychange", handleVisibilityChange);
  }

  return () => {
    unsubscribe();
    if (typeof window !== "undefined") {
      window.removeEventListener("visibilitychange", handleVisibilityChange);
    }
  };
};

/**
 * Generates a cross-domain URL with an embedded short-lived sync token as a fallback.
 */
export const buildCrossDomainUrl = (targetUrl, currentUser) => {
  try {
    if (!targetUrl) return "";
    const url = new URL(targetUrl, typeof window !== "undefined" ? window.location.origin : undefined);

    const user = currentUser;
    if (user && user.uid) {
      const sessionData = typeof user.toJSON === "function" ? user.toJSON() : null;
      if (sessionData) {
        const payload = {
          uid: user.uid,
          email: user.email || "",
          displayName: user.displayName || "",
          ts: Date.now(),
          session: sessionData,
        };
        const token = encodeSessionPayload(payload);
        if (token) {
          url.searchParams.set("_slx_sync", token);
        }
      }
    }
    return url.toString();
  } catch (e) {
    return targetUrl;
  }
};
